import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import { Field, useFormikContext } from "formik";

import API from "../../../../api/axios";
import SmartSearchSelect from "../../../../components/Smart/SmartSearchSelect";
import PhoneInput from "../../../../components/Form/PhoneInput";
import { handleSafeSelectChange } from "../../../../utils/handleSafeSelectChange";

import FormSection from "../FormSection";

const OverviewTab = ({ isEdit, countryOptions }) => {
    const { values, setFieldValue } = useFormikContext();

    const [currencies, setCurrencies] = useState([]);
    const [localeOptions, setLocaleOptions] = useState([]);

    useEffect(() => {
        const fetchCurrencies = async () => {
            try {
                const { data } = await API("/currencies");
                setCurrencies(data);
            } catch (err) {
                console.error("Failed to fetch currencies:", err);
            }
        };

        const fetchLocales = async () => {
            try {
                const { data } = await API("/location/locales");
                setLocaleOptions(data);
            } catch (err) {
                console.error("Failed to fetch locales:", err);
            }
        };

        fetchCurrencies();
        fetchLocales();
    }, []);

    const currenciesOptions = useMemo(() => {
        return (currencies || []).map((c) => ({
            value: c.code,
            label: `${c.code} — ${c.name} (${c.symbol})`,
        }));
    }, [currencies]);

    useEffect(() => {
        console.log(values?.pricingSnapshot?.calculation?.customerCurrencyCode);
        console.log(values?.pricingSnapshot?.calculation?.exchangeRate);
    }, [values?.pricingSnapshot?.calculation?.customerCurrencyCode, values?.pricingSnapshot?.calculation?.exchangeRate]);




    return (
        <FormSection title="Overview">
            <Row>
                {isEdit && (
                    <Col md={6} className="mb-3">
                        <div className="fw-5 mb-1">Booking Number:</div>
                        <div className="d-flex align-items-center mb-3">
                            {values?.rentalOrder?.referenceNumber}
                        </div>
                    </Col>
                )}

                <Col md={6} className="mb-3">
                    <Field name="customerSnapshot.name">
                        {({ field, meta }) => (
                            <Form.Group className="mb-3">
                                <Form.Label>Customer Name</Form.Label>
                                <Form.Control
                                    {...field}
                                    value={field.value ?? ""}
                                    type="text"
                                    isInvalid={meta.touched && !!meta.error}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {meta.error}
                                </Form.Control.Feedback>
                            </Form.Group>
                        )}
                    </Field>
                </Col>

                <Col md={6} className="mb-3">
                    <Field name="customerSnapshot.lastName">
                        {({ field, meta }) => (
                            <Form.Group className="mb-3">
                                <Form.Label>Customer Last Name</Form.Label>
                                <Form.Control
                                    {...field}
                                    value={field.value ?? ""}
                                    type="text"
                                    isInvalid={meta.touched && !!meta.error}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {meta.error}
                                </Form.Control.Feedback>
                            </Form.Group>
                        )}
                    </Field>
                </Col>

                <Col md={6} className="mb-3">
                    <Field name="customerSnapshot.email">
                        {({ field, meta }) => (
                            <Form.Group className="mb-3">
                                <Form.Label>Customer Email</Form.Label>
                                <Form.Control
                                    {...field}
                                    value={field.value ?? ""}
                                    type="text"
                                    isInvalid={meta.touched && !!meta.error}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {meta.error}
                                </Form.Control.Feedback>
                            </Form.Group>
                        )}
                    </Field>
                </Col>

                <Col md={6} className="mb-3">
                    <Form.Group className="mb-3">
                        <Form.Label>Customer Country</Form.Label>
                        <SmartSearchSelect
                            currentID={values?.customerSnapshot?.country?.id || ""}
                            options={countryOptions}
                            onChange={async (selectedCountry) => {
                                handleSafeSelectChange(
                                    values.customerSnapshot?.country?.id,
                                    selectedCountry,
                                    ({ nextValue }) => {
                                        setFieldValue(
                                            "customerSnapshot.country.id",
                                            nextValue || "",
                                            false
                                        );
                                        setFieldValue(
                                            "customerSnapshot.country.name",
                                            selectedCountry.label || "",
                                            false
                                        );
                                    }
                                );
                            }}
                            placeholder="Select country..."
                        />
                    </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                    <Field name="customerSnapshot.license">
                        {({ field, meta }) => (
                            <Form.Group className="mb-3">
                                <Form.Label>Customer License</Form.Label>
                                <Form.Control
                                    {...field}
                                    value={field.value ?? ""}
                                    type="text"
                                    isInvalid={meta.touched && !!meta.error}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {meta.error}
                                </Form.Control.Feedback>
                            </Form.Group>
                        )}
                    </Field>
                </Col>

                <Col md={6} className="mb-3">
                    <Field name="customerSnapshot.language">
                        {({ field }) => (
                            <Form.Group className="mb-3">
                                <Form.Label>Customer Language</Form.Label>
                                <Form.Select {...field} value={field.value ?? ""}>
                                    <option value="" disabled>
                                        Select Language
                                    </option>
                                    {localeOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}
                    </Field>
                </Col>

                <Col md={6}>
                    <PhoneInput
                        label="Customer Phone"
                        prefixField="customerSnapshot.prefix"
                        phoneField="customerSnapshot.phone"
                    />
                </Col>

                <Col md={6} className="mb-3">
                    <Field name="pricingSnapshot.calculation.customerCurrencyCode">
                        {({ field }) => {
                            const supplier =
                                values?.pricingSnapshot?.calculation?.supplierCurrencyCode || "";
                            const customer =
                                values?.pricingSnapshot?.calculation?.customerCurrencyCode || "";
                            const rate = values?.pricingSnapshot?.calculation?.exchangeRate;

                            const showRate = supplier && customer && rate !== "" && rate != null;

                            return (
                                <Form.Group className="mb-3">
                                    <Form.Label>Customer Currency</Form.Label>
                                    <Form.Select {...field} value={field.value || ""}>
                                        <option value="" disabled>
                                            Select Currency
                                        </option>
                                        {currenciesOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Form.Select>

                                    {showRate && (
                                        <Form.Text className="text-muted">
                                            {supplier} to {customer} {rate}
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            );
                        }}
                    </Field>
                </Col>

                <Col md={12} className="mb-3">
                    <Field name="customerSnapshot.comment">
                        {({ field, meta }) => (
                            <Form.Group className="mb-3">
                                <Form.Label>Customer Comment</Form.Label>
                                <Form.Control
                                    {...field}
                                    value={field.value ?? ""}
                                    as="textarea"
                                    rows={3}
                                    isInvalid={meta.touched && !!meta.error}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {meta.error}
                                </Form.Control.Feedback>
                            </Form.Group>
                        )}
                    </Field>
                </Col>
            </Row>
        </FormSection>
    );
};

export default OverviewTab;